/**
 * 船機更新模擬——「船機現在來問，會拿到什麼」。
 *
 * ## 為什麼要有這一頁
 *
 * `/api/cms/update` 的回應是我方與船機之間唯一的介面，但它平常只有船機看得到，
 * 而船一個月才靠港幾次。發布完之後「這一版到底送了什麼下去」在 CMS 裡沒有任何
 * 地方看得出來——直到船靠岸、東西沒更新，才開始回頭猜。
 *
 * 這一頁把那份 JSON 原樣攤開。**逐字**攤開：鍵名、`download_url` 的六個分類、
 * presigned 連結、全域那一筆，全都是船機真正會收到的內容，不是重新整理過的摘要。
 * 對接韌體時要比對的正是這個——差一個鍵名船機就解析不出來。
 *
 * ## 內容取自已發布的版本，不是編輯中的草稿
 *
 * 回應反映的是目前 **active 的 release manifest**。剛改完還沒發布的景點不會出現
 * 在這裡，這不是 bug——船機本來就只看得到已發布的內容。要預覽草稿請到景點編輯頁
 * 的「產出預覽」分頁。
 */

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../components/Button';
import {
  hasUpdateInfo,
  simulateCmsUpdate,
  type CmsUpdateAttraction,
  type CmsUpdateEnvelope,
} from '../lib/api-client';

const OP_COLOR: Record<string, string> = {
  add: '#059669',
  modify: '#2563eb',
  delete: '#dc2626',
};

/** 一筆 attraction 的下載檔數（各分類相加）。 */
function fileCount(a: CmsUpdateAttraction): number {
  if (!a.download_url) return 0;
  return Object.values(a.download_url).reduce((sum, list) => sum + list.length, 0);
}

export function DisplayUpdateSimulationPage(): JSX.Element {
  const [version, setVersion] = useState('');
  /** 空字串 = 不帶 lang，也就是三個語系全給。這是船機的預設行為。 */
  const [lang, setLang] = useState('');
  const [result, setResult] = useState<CmsUpdateEnvelope | null>(null);
  const [copied, setCopied] = useState(false);

  const run = useMutation({
    mutationFn: (v: { version: string; lang: string }) => simulateCmsUpdate(v),
    onSuccess: (data) => {
      setResult(data);
      setCopied(false);
    },
  });

  const json = result ? JSON.stringify(result, null, 2) : '';
  const info = result && hasUpdateInfo(result.data.info) ? result.data.info : null;
  const attractions = info?.attractions ?? [];
  const totalFiles = attractions.reduce((s, a) => s + fileCount(a), 0);

  const copy = (): void => {
    void navigator.clipboard.writeText(json).then(() => setCopied(true));
  };

  const download = (): void => {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cms-update-${info?.version || 'none'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">船機更新模擬</h1>
      </div>

      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 6,
          padding: 12,
          margin: '12px 0',
          fontSize: 13,
          maxWidth: 1000,
        }}
      >
        模擬船機打 <code>POST /api/cms/update</code> 會收到的<strong>完整 JSON</strong>，
        與船機走的是同一份組裝，逐字相同。<strong>不寫入任何東西，也不留遙測紀錄</strong>
        ——所以不會污染「這台船最後一次同步是什麼時候」的判斷依據。
        <br />
        內容取自目前<strong>已發布</strong>的版本；還沒發布的景點編輯不會出現在這裡。
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          margin: '12px 0',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          船機回報的版本碼
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="例：20260813_2（留空 = 全新安裝）"
            style={{ padding: 8, width: 260 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          語系
          <select value={lang} onChange={(e) => setLang(e.target.value)} style={{ padding: 8 }}>
            {/* 不帶 = 三語系全給，這是船機的預設行為，所以擺第一個也是預設值 */}
            <option value="">不帶（＝所有語系）</option>
            <option value="zh">zh</option>
            <option value="en">en</option>
            <option value="jp">jp</option>
          </select>
        </label>
        <Button variant="primary" onClick={() => run.mutate({ version, lang })}>
          {run.isPending ? '模擬中…' : '模擬'}
        </Button>
        {info && info.version !== '' && info.version !== version && (
          /*
           * 更新完再問一次應該要收斂成 update_required=0。船機的同步邏輯是
           * 「問→下載→下次再問」，這個按鈕把第二輪省成一次點擊。
           */
          <Button
            variant="secondary"
            onClick={() => {
              setVersion(info.version);
              run.mutate({ version: info.version, lang });
            }}
          >
            ↻ 以 {info.version} 再問一次（應為無更新）
          </Button>
        )}
      </div>

      <div style={{ fontSize: 12, color: '#9ca3af', margin: '4px 0 12px' }}>
        版本碼格式為 <code>yyyymmdd_當日第幾版</code>。認不得的版本一律視為全新安裝，所有景點都會是{' '}
        <code>add</code>。語系<strong>不帶就是三種語系全給</strong>；指定語系只會拿到該語系的
        資料夾，但「整個景點從船上移除」那種 <code>delete</code> 任何語系都收得到。
      </div>

      {run.error && <div className="error-banner">{(run.error as Error).message}</div>}

      {result && (
        <>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', margin: '12px 0' }}>
            <Stat label="status" value={result.status} bad={result.status !== 'success'} />
            <Stat label="岸上版本" value={info?.version || '（尚未發布過）'} />
            <Stat label="update_required" value={String(info?.update_required ?? '—')} />
            <Stat label="attractions" value={`${attractions.length} 筆`} />
            <Stat label="下載檔數" value={`${totalFiles} 個`} />
          </div>

          {result.data.message && <div className="error-banner">{result.data.message}</div>}

          {attractions.length === 0 && result.status === 'success' && (
            <div style={{ fontSize: 13, color: '#6b7280', margin: '12px 0' }}>
              沒有任何變更——船機收到這份回應後不會下載任何東西。
            </div>
          )}

          {attractions.length > 0 && (
            <div className="table-card" style={{ maxWidth: 1000, overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>op</th>
                    <th>attraction_id</th>
                    <th>名稱</th>
                    <th>target_path</th>
                    <th>檔數</th>
                    <th>分類</th>
                  </tr>
                </thead>
                <tbody>
                  {attractions.map((a, i) => (
                    <tr key={`${a.attraction_id}-${a.target_path ?? ''}-${i}`}>
                      <td>
                        <span style={{ color: OP_COLOR[a.op] ?? '#374151', fontWeight: 600 }}>
                          {a.op}
                        </span>
                      </td>
                      <td>{a.attraction_id}</td>
                      <td>{a.attraction_name}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {a.target_path ?? (a.delete_paths ?? []).join('、')}
                      </td>
                      <td>{fileCount(a)}</td>
                      <td style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
                        {a.download_url
                          ? Object.entries(a.download_url)
                              .filter(([, list]) => list.length > 0)
                              .map(([k, list]) => `${k}:${list.length}`)
                              .join(' ')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              margin: '16px 0 6px',
              maxWidth: 1000,
            }}
          >
            <strong style={{ fontSize: 13 }}>原始回應</strong>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              船機收到的就是這一份（{json.length.toLocaleString()} 字元）
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <Button variant="secondary" onClick={copy}>
                {copied ? '已複製' : '複製'}
              </Button>
              <Button variant="secondary" onClick={download}>
                下載 .json
              </Button>
            </span>
          </div>
          <pre
            style={{
              maxWidth: 1000,
              maxHeight: '55vh',
              overflow: 'auto',
              background: '#0f172a',
              color: '#e2e8f0',
              padding: 12,
              borderRadius: 6,
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {json}
          </pre>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, bad }: { label: string; value: string; bad?: boolean }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#9ca3af' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: bad ? '#dc2626' : '#111827' }}>
        {value}
      </div>
    </div>
  );
}
